// Mock data for LearnixTa Career Counselling SaaS

export const heroData = {
  headline: "Stop Guessing Your Future. Start Building It.",
  subheadline: "Get career clarity through psychometric tests, role reality checks, skill roadmaps, and AI-powered guidance — designed for Indian students.",
  primaryCTAs: [
    { text: "Explore after 10th / Inter", link: "/pathways/after-10th" },
    { text: "Choose specialization (B.Tech / BBA / MBA)", link: "/pathways/first-year" },
    { text: "Switch to a job role", link: "/pathways/switch" }
  ],
  secondaryCTAs: [
    { text: "Try 5-min Fit Test", link: "/fit-test" },
    { text: "Ask AI Mentor", link: "/ai-mentor" }
  ],
  heroImage: "https://images.unsplash.com/photo-1761781342506-30f68c930a16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxzdHVkZW50cyUyMGdyYWR1YXRpb24lMjBzdWNjZXNzJTIwdW5pdmVyc2l0eSUyMGNlbGVicmF0aW9ufGVufDB8fHx8MTc2OTIwMjc5NXww&ixlib=rb-4.1.0&q=85"
};

export const howItWorksData = {
  title: "How LearnixTa Works",
  subtitle: "Stop guessing. Get clarity in 4 simple steps.",
  steps: [
    {
      id: 1,
      icon: "ClipboardCheck",
      title: "Fit Test",
      description: "Psychometric + aptitude + work-style assessment to understand what truly fits you."
    },
    {
      id: 2,
      icon: "Eye",
      title: "Reality Check",
      description: "See day-in-life, tools, challenges of roles before you commit years to them."
    },
    {
      id: 3,
      icon: "Map",
      title: "Skills Map",
      description: "Know exactly what skills you need + proof projects to build them."
    },
    {
      id: 4,
      icon: "TrendingUp",
      title: "Roadmap + AI Risk",
      description: "Weekly learning plan with AI impact assessment for future-safe careers."
    }
  ]
};

export const pathwaysData = [
  {
    id: 1,
    title: "After 10th / Inter — Explore & Decide",
    description: "Not sure about MPC, BiPC, Commerce, or Arts? See exactly where each stream leads in India's 2026 job market — not what it led to 10 years ago. Then take the fit test and decide with actual data.",
    link: "/pathways/after-10th",
    image: "https://images.unsplash.com/photo-1683727609398-7693457f4717",
    tag: "Most Popular"
  },
  {
    id: 2,
    title: "B.Tech / BBA / MBA — Choose Specialization",
    description: "Stop guessing which specialization to pick. See what each branch actually leads to — real roles, real salaries, real AI risk — before you lock in 4 years of your life.",
    link: "/pathways/first-year",
    image: "https://images.pexels.com/photos/12585521/pexels-photo-12585521.jpeg"
  },
  {
    id: 3,
    title: "Final Year / Switch — Skills > Degree",
    description: "{ROLE_COUNT} roles across engineering, management, and specialized domains. Every role has eligibility check, 8-stage learning path, AI risk score, portfolio project, and an interview explanation template. The most complete career role library built for Indian students.",
    link: "/pathways/switch",
    image: "https://images.unsplash.com/photo-1659080927204-de39f5fdfb02"
  },
  {
    id: 4,
    title: "Careers in Telangana & AP",
    description: "Explore careers rooted in local opportunities — government jobs, startups, industries in TS & AP.",
    link: "/explore/careers-ts-ap",
    image: "https://images.unsplash.com/photo-1596495868845-63031cb496da"
  },
  {
    id: 5,
    title: "AI Pathway (New-Age Tech)",
    description: "Learn AI, ML, Data Science with reality check on what AI will automate and what it won't.",
    link: "/explore/ai",
    image: "https://images.pexels.com/photos/9242823/pexels-photo-9242823.jpeg",
    tag: "Trending"
  },
  {
    id: 6,
    title: "Entrepreneurship Pathway",
    description: "Want to build your own thing? See what it really takes — skills, mindset, and reality.",
    link: "/explore/entrepreneurship",
    image: "https://images.pexels.com/photos/9829493/pexels-photo-9829493.jpeg"
  }
];

export const programsData = [
  {
    id: 1,
    title: "Engineering Programs",
    description: "CSE samplers → specialization tracks (AI/ML, Full Stack, DevOps, Data) with real tools and projects.",
    features: ["Branch Reality Check", "Skill Roadmaps", "Proof Projects", "AI Risk Assessment"],
    link: "/programs/engineering"
  },
  {
    id: 2,
    title: "Management Programs",
    description: "BBA/MBA with real business artifacts — case studies, pitch decks, financial models, not just theory.",
    features: ["Specialization Clarity", "Real Artifacts", "Industry Projects", "Career Mapping"],
    link: "/programs/management"
  }
];

export const featuresData = [
  {
    id: 1,
    icon: "BookOpen",
    title: "Learning Resources",
    description: "Curated courses, tutorials, and guides for every skill on your roadmap."
  },
  {
    id: 2,
    icon: "FolderKanban",
    title: "Project Showcase",
    description: "Build proof projects with rubrics and showcase them to land opportunities."
  },
  {
    id: 3,
    icon: "Award",
    title: "Skill Certifications",
    description: "Rubric-based skill validation that actually matters to employers."
  },
  {
    id: 4,
    icon: "Users",
    title: "Mentorship",
    description: "Get guidance from professionals who've been there and done that."
  },
  {
    id: 5,
    icon: "MessageCircle",
    title: "Peer Community",
    description: "Connect with students on similar paths. Share, learn, and grow together."
  }
];

export const roleLibraryData = {
  title: "Explore 500+ Career Roles",
  subtitle: "See the reality, skills needed, and AI impact for every role before you commit.",
  roles: [
    {
      id: 1,
      name: "Full Stack Developer",
      description: "Build complete web applications from frontend to backend.",
      skills: ["React", "Node.js", "MongoDB"],
      aiRisk: "Low"
    },
    {
      id: 2,
      name: "Data Analyst",
      description: "Turn raw data into insights that drive business decisions.",
      skills: ["Python", "SQL", "Tableau"],
      aiRisk: "Medium"
    },
    {
      id: 3,
      name: "Digital Marketing Specialist",
      description: "Create and manage campaigns across digital channels.",
      skills: ["SEO", "Analytics", "Content"],
      aiRisk: "Medium"
    },
    {
      id: 4,
      name: "UI/UX Designer",
      description: "Design user experiences that are beautiful and functional.",
      skills: ["Figma", "Research", "Prototyping"],
      aiRisk: "Low"
    },
    {
      id: 5,
      name: "DevOps Engineer",
      description: "Automate deployment and manage infrastructure at scale.",
      skills: ["Docker", "Kubernetes", "CI/CD"],
      aiRisk: "Low"
    },
    {
      id: 6,
      name: "Content Writer",
      description: "Create compelling content that engages and converts.",
      skills: ["Writing", "SEO", "Research"],
      aiRisk: "High"
    },
    {
      id: 7,
      name: "Machine Learning Engineer",
      description: "Build AI models that solve real-world problems.",
      skills: ["Python", "TensorFlow", "Statistics"],
      aiRisk: "Low"
    },
    {
      id: 8,
      name: "Product Manager",
      description: "Own product strategy and drive execution with teams.",
      skills: ["Strategy", "Analytics", "Communication"],
      aiRisk: "Low"
    }
  ]
};

export const aiMentorData = {
  title: "Meet Your AI Career Mentor",
  subtitle: "Not a generic chatbot. An AI trained on career realities, skills, and Indian opportunities.",
  prompts: [
    "Which B.Tech specialization fits me — CSE-AI vs Data Science?",
    "I'm in final year Mechanical — which future-ready roles should I switch to?",
    "What's the AI Risk score on my current role?",
    "Bank Clerk vs Bank PO — which path is right for me?",
    "My parents want engineering but I want design — how do I show them?",
    "How to start a juice stall under ₹5,000 — any PMEGP or MUDRA scheme?",
    "Which AI career suits a non-coder — Prompt Engineer or AI PM?",
    "Best skilled-service career via Urban Company in Hyderabad?"
  ]
};

export const pricingData = {
  students: [
    {
      id: 1,
      name: "Basic",
      price: "Free",
      period: "Forever",
      features: [
        "5-min Fit Test",
        "Browse 50+ roles",
        "Basic skill roadmaps",
        "Community access",
        "AI Mentor (3 questions/day)"
      ],
      cta: "Start free →",
      link: "/signup",
      popular: false
    },
    {
      id: 2,
      name: "Essential",
      price: "₹2,999",
      period: "Your 8-week career launch",
      features: [
        "Full psychometric assessment",
        "Unlimited role exploration",
        "1 complete learning track",
        "Proof project with rubric + mentor review",
        "AI Mentor (unlimited)",
        "Skill certification",
        "Priority community support"
      ],
      cta: "Start my career launch →",
      link: "/signup?plan=essential",
      popular: true
    },
    {
      id: 3,
      name: "Premium",
      price: "₹9,999",
      period: "Annual membership",
      features: [
        "Everything in Essential",
        "Unlimited learning tracks",
        "Multiple proof projects",
        "1-on-1 mentorship (2 sessions/month)",
        "Career switch roadmap",
        "Interview prep resources",
        "Job referrals network"
      ],
      cta: "Go Premium →",
      link: "/signup?plan=premium",
      popular: false
    }
  ],
  partners: [
    {
      id: 1,
      name: "Inter Colleges",
      price: "Custom",
      description: "Help students choose the right stream after 10th with data-driven clarity.",
      features: [
        "Bulk psychometric assessments",
        "Stream selection workshops",
        "Parent-student reports",
        "Career counselor dashboard",
        "Custom branding"
      ],
      cta: "Contact Us",
      link: "/contact?type=college"
    },
    {
      id: 2,
      name: "B.Tech / BBA / MBA Colleges",
      price: "Custom",
      description: "Give your students specialization clarity and employability skills.",
      features: [
        "Specialization fit assessments",
        "Skill development tracks",
        "Placement readiness program",
        "Analytics dashboard",
        "White-label option"
      ],
      cta: "Contact Us",
      link: "/contact?type=college"
    }
  ]
};

export const whoWeServeData = [
  {
    id: 1,
    icon: "GraduationCap",
    title: "Students (After 10th/Inter)",
    description: "Choose the right stream — MPC, BiPC, Commerce, or Arts — with clarity on what each leads to.",
    link: "/pathways/after-10th"
  },
  {
    id: 2,
    icon: "BookMarked",
    title: "College Students",
    description: "Choose specialization, build real skills, and become employable — not just educated.",
    link: "/pathways/first-year"
  },
  {
    id: 3,
    icon: "Zap",
    title: "Final Year / Switchers",
    description: "Skills matter more than your degree. Choose a role, build proof, land the job.",
    link: "/pathways/switch"
  },
  {
    id: 4,
    icon: "Building2",
    title: "Colleges/Institutes",
    description: "Help your students with career clarity, skill development, and employability.",
    link: "/contact?type=college"
  }
];

export const testimonialsData = [
  {
    id: 1,
    name: "Rahul M.",
    role: "B.Tech 1st Year, JNTUH",
    text: "I was confused between CSE and ECE. The reality check showed me what coding actually looks like. Now I'm confident in CSE and already learning React.",
    avatar: null
  },
  {
    id: 2,
    name: "Sneha P.",
    role: "After 10th, Hyderabad",
    text: "My parents wanted me to take MPC, but the fit test showed I'm better with commerce + creativity. Now pursuing BBA and loving it.",
    avatar: null
  },
  {
    id: 3,
    name: "Karthik R.",
    role: "Final Year Mechanical, Switched to Data Analytics",
    text: "Mechanical wasn't for me. LearnixTa's roadmap helped me build a data portfolio in 3 months. Got placed as a Data Analyst.",
    avatar: null
  }
];

export const faqData = [
  {
    id: 1,
    question: "What makes LearnixTa different from other career platforms?",
    answer: "We don't just test you and give a report. We show you the reality of each career (day-in-life, tools, challenges), give you a skill roadmap with proof projects, and help you understand AI's impact on that career."
  },
  {
    id: 2,
    question: "Is the Fit Test really accurate?",
    answer: "Our psychometric test combines aptitude, interests, work style, and real-world preferences. It's designed specifically for Indian students navigating the education system. But remember — it's a guide, not a final verdict."
  },
  {
    id: 3,
    question: "Can I change my career path later?",
    answer: "Absolutely. Career paths are not permanent. We help you switch roles by showing you what skills to learn and what projects to build. Many students discover their real interest after trying samplers."
  },
  {
    id: 4,
    question: "Do you provide job placements?",
    answer: "We don't guarantee placements, but we help you become placement-ready. Our skill tracks, proof projects, and rubric-based certifications make you employable. Premium members get access to job referral networks."
  },
  {
    id: 5,
    question: "Is this only for engineering students?",
    answer: "Not at all. We cover streams across MPC, BiPC, Commerce, Arts — and careers in engineering, management, design, content, healthcare, entrepreneurship, and more."
  },
  {
    id: 6,
    question: "What is AI Risk assessment?",
    answer: "We analyze which parts of each role AI can automate and which parts will remain human-centric. This helps you choose future-safe careers and focus on skills that AI can't replace."
  },
  {
    id: 7,
    question: "Can parents access their child's progress?",
    answer: "Yes. Parents get regular reports on assessments, learning progress, and skill development. We believe career decisions should be made together, not forced."
  }
];

export const footerData = {
  pathways: [
    { name: "After 10th / Inter", link: "/pathways/after-10th" },
    { name: "B.Tech / BBA / MBA", link: "/pathways/first-year" },
    { name: "Final Year / Switch", link: "/pathways/switch" },
    { name: "Life Skills — For Every Future", link: "/life-skills" }
  ],
  explore: [
    { name: "Careers in Telangana & AP", link: "/explore/careers-ts-ap" },
    { name: "AI Pathway", link: "/explore/ai" },
    { name: "Entrepreneurship", link: "/explore/entrepreneurship" }
  ],
  programs: [],
  // Platform (Specializations / Community) + Partners columns temporarily hidden.
  platform: [],
  partners: [],
  company: [
    { name: "About Us", link: "/about" }
  ],
  legal: [
    { name: "Terms of Service", link: "/terms" },
    { name: "Privacy Policy", link: "/privacy" },
    { name: "Refund Policy", link: "/refunds" },
    { name: "Cookie Policy", link: "/cookies" },
    { name: "AI Disclaimer", link: "/ai-disclaimer" },
    { name: "Acceptable Use", link: "/acceptable-use" }
  ]
};
